import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, Minus, Heart, MessageCircle, Send, Loader2, Shield } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { AICCIMethodologyLink } from "../dashboard/AICCIMethodology";
import axios from "axios";
import { trackEvent } from "../../lib/track";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* Inject Mirror animations once */
const MIR_STYLE_ID = "mirror-anim-styles";
if (typeof document !== "undefined" && !document.getElementById(MIR_STYLE_ID)) {
  const s = document.createElement("style");
  s.id = MIR_STYLE_ID;
  s.textContent = `
    @keyframes mir-rise { 0% { transform: translateY(30px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
    @keyframes mir-glow-pulse { 0%,100% { box-shadow: 0 0 20px rgba(139,92,246,0.1); } 50% { box-shadow: 0 0 40px rgba(139,92,246,0.2); } }
    @keyframes mir-bar-grow { from { width: 0; } to { width: var(--bar-w); } }
    .mir-rise { animation: mir-rise 0.6s ease-out both; }
    .mir-card-glow { animation: mir-glow-pulse 4s ease-in-out infinite; }
  `;
  document.head.appendChild(s);
}

const clean = (s) => (s || "").replace(/\.\s*$/, "").trim();

const FRAMEWORK_DESC = {
  maslow: "Abraham Maslow's theory that human needs form a pyramid, from basic survival to self-actualization",
  perez: "Carlota Perez's model of how major technologies transform economies in predictable waves",
  schwartz: "Shalom Schwartz's theory mapping universal human values along two axes: self vs others, change vs stability",
  sdgs: "The United Nations' 17 goals for peace, prosperity, and planet by 2030",
  spiral: "A model of evolving worldviews, from survival instincts to holistic global consciousness",
};

const EMOTIONS = [
  { id: "hopeful", label: "Hopeful", color: "#34d399" },
  { id: "concerned", label: "Concerned", color: "#f87171" },
  { id: "inspired", label: "Inspired", color: "#a78bfa" },
  { id: "unsure", label: "Unsure", color: "#94a3b8" },
];

const DirectionIcon = ({ dir }) => {
  if (dir === "up") return <TrendingUp className="w-4 h-4 text-emerald-400" />;
  if (dir === "down") return <TrendingDown className="w-4 h-4 text-red-400" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export const MirrorPage = () => {
  const [trends, setTrends] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [responses, setResponses] = useState(null);
  const [vision2050, setVision2050] = useState(null);
  const [wordcloud, setWordcloud] = useState(null);
  const [aicci, setAicci] = useState(null);
  const [aicciInsights, setAicciInsights] = useState(null);
  const [trajectory, setTrajectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userResponse, setUserResponse] = useState("");
  const [selectedEmotion, setSelectedEmotion] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Phase 1: Fast data (trends + AICCI) — show hero immediately
      try {
        const [t, a] = await Promise.allSettled([
          axios.get(`${API}/mirror/trends`),
          axios.get(`${API}/aicci`),
        ]);
        if (t.status === "fulfilled") setTrends(t.value.data);
        if (a.status === "fulfilled") setAicci(a.value.data);
      } catch {}
      setLoading(false); // Hero + AICCI visible now

      // Fire ALL remaining calls in parallel — each section flashes in as its data arrives
      await Promise.allSettled([
        axios.get(`${API}/mirror/prediction`).then(r => setPrediction(r.data)).catch(() => {}),
        axios.get(`${API}/mirror/responses?question_id=main`).then(r => setResponses(r.data)).catch(() => {}),
        axios.get(`${API}/mirror/vision2050`).then(r => setVision2050(r.data)).catch(() => {}),
        axios.get(`${API}/mirror/wordcloud`).then(r => setWordcloud(r.data)).catch(() => {}),
        axios.get(`${API}/aicci/insights`).then(r => setAicciInsights(r.data)).catch(() => {}),
        axios.get(`${API}/mirror/trajectory`).then(r => setTrajectory(r.data)).catch(() => {}),
      ]);
    };
    load();
  }, []);

  const handleSubmitResponse = async () => {
    if (!userResponse.trim() && !selectedEmotion) return;
    setSubmitting(true);
    trackEvent("mirror_respond", "mirror", selectedEmotion || "text_only");
    try {
      await axios.post(`${API}/mirror/respond`, {
        question_id: "main",
        response: userResponse.trim(),
        emotion: selectedEmotion,
      });
      setSubmitted(true);
      // Refresh responses + word cloud immediately
      const [r, w] = await Promise.allSettled([
        axios.get(`${API}/mirror/responses?question_id=main`),
        axios.get(`${API}/mirror/wordcloud?bust=${Date.now()}`),
      ]);
      if (r.status === "fulfilled") setResponses(r.data);
      if (w.status === "fulfilled") setWordcloud(w.value.data);
    } catch {}
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-violet-500/10 flex items-center justify-center mb-4" style={{ animation: "mir-glow-pulse 2s ease-in-out infinite", border: "1px solid rgba(139,92,246,0.3)" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(200,180,255,0.7)", boxShadow: "0 0 12px rgba(139,92,246,0.5)", animation: "cst-float 2s ease-in-out infinite" }} />
          </div>
          <p className="text-sm text-slate-400">The Mirror is reflecting...</p>
        </div>
      </div>
    );
  }

  const latest = trends?.latest_year || {};
  const earliest = trends?.earliest_year || {};

  return (
    <div className="space-y-8 animate-fade-in" data-testid="mirror-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl py-12 px-8 text-center mir-rise mir-card-glow"
        style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #0c1222 50%, #0a0e1a 100%)" }}>
        <div className="relative z-10">
          <div className="w-20 h-20 mx-auto mb-5 relative" style={{ animation: "mir-glow-pulse 3s ease-in-out infinite" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", border: "2px solid rgba(139,92,246,0.5)", background: "radial-gradient(circle at 35% 35%, rgba(200,180,255,0.2) 0%, rgba(139,92,246,0.08) 40%, rgba(30,20,60,0.4) 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 30px rgba(139,92,246,0.2), inset 0 0 30px rgba(139,92,246,0.06)", position: "relative", overflow: "hidden" }}>
              {/* Glass reflection arc */}
              <div style={{ position: "absolute", top: 8, left: 12, width: 28, height: 14, borderRadius: "50%", background: "linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%)", transform: "rotate(-25deg)" }} />
              {/* Center glow dot */}
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(200,180,255,0.6)", boxShadow: "0 0 12px rgba(139,92,246,0.6)" }} />
            </div>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-white mb-2">The Mirror</h2>
          <p className="text-xs font-bold text-violet-400 uppercase tracking-[0.25em] mb-4">What AI tells us about ourselves</p>
          {prediction && (
            <p className="text-lg text-slate-200 max-w-2xl mx-auto leading-relaxed font-light italic">
              "{prediction.headline}"
            </p>
          )}
        </div>
      </div>

      {/* LLM Reflection */}
      {prediction ? (
        <div className="bg-white/[0.02] border border-violet-500/15 rounded-2xl p-6 mir-rise" style={{ animationDelay: "0.2s" }} data-testid="mirror-reflection">
          <p className="text-sm text-slate-300 leading-relaxed mb-5">{prediction.reflection}</p>
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="bg-white/[0.03] rounded-xl p-4 text-center border border-white/[0.06]">
              <div className="flex items-center justify-center gap-2 mb-1">
                <DirectionIcon dir={prediction.care_direction} />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Care AI</span>
              </div>
              <span className="text-2xl font-bold text-emerald-400">{latest.care_pct || "?"}%</span>
              <p className="text-[10px] text-slate-500 mt-1">of apps built for wellbeing</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-4 text-center border border-white/[0.06]">
              <div className="flex items-center justify-center gap-2 mb-1">
                <DirectionIcon dir={prediction.extraction_direction} />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Extraction</span>
              </div>
              <span className="text-2xl font-bold text-red-400">{latest.extraction_pct || "?"}%</span>
              <p className="text-[10px] text-slate-500 mt-1">of apps extracting value</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-4 text-center border border-white/[0.06]">
              <div className="flex items-center justify-center gap-2 mb-1">
                <DirectionIcon dir={prediction.diversity_direction} />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Diversity</span>
              </div>
              <span className="text-2xl font-bold text-violet-400">{trends?.diversity_trends?.slice(-1)[0]?.diversity_pct || "?"}%</span>
              <p className="text-[10px] text-slate-500 mt-1">diverse founding teams</p>
            </div>
          </div>
          {/* Industry insight */}
          {prediction.industry_insight && (
            <div className="bg-amber-500/[0.06] border border-amber-500/15 rounded-xl p-4 mt-4">
              <p className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2">Industry Consciousness</p>
              <p className="text-sm text-slate-300 leading-relaxed italic">{prediction.industry_insight}</p>
            </div>
          )}
          {/* Research insight */}
          {prediction.research_insight && (
            <div className="bg-purple-500/[0.06] border border-purple-500/15 rounded-xl p-4 mt-4">
              <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-2">Research Intelligence</p>
              <p className="text-sm text-slate-300 leading-relaxed italic">{prediction.research_insight}</p>
            </div>
          )}
          {/* Future prediction */}
          <div className="bg-violet-500/[0.06] border border-violet-500/15 rounded-xl p-4 mt-4">
            <p className="text-xs font-bold text-violet-400 uppercase tracking-wider mb-2">Looking Ahead to 2028</p>
            <p className="text-sm text-slate-300 leading-relaxed">{prediction.future_prediction}</p>
          </div>
        </div>
      ) : (
        <div className="bg-white/[0.02] border border-white/[0.04] rounded-2xl p-6 animate-pulse">
          <div className="h-3 bg-white/[0.06] rounded w-2/3 mb-4" />
          <div className="h-2 bg-white/[0.04] rounded w-full mb-2" />
          <div className="h-2 bg-white/[0.04] rounded w-5/6 mb-2" />
          <div className="h-2 bg-white/[0.04] rounded w-3/4 mb-5" />
          <div className="grid grid-cols-3 gap-3">
            {[1,2,3].map(i => <div key={i} className="h-16 bg-white/[0.03] rounded-xl" />)}
          </div>
          <p className="text-[10px] text-slate-500 text-center mt-4">Generating reflection from {aicci?.total_apps?.toLocaleString() || "90,000+"} entities...</p>
        </div>
      )}

      {/* ── AICCI Tracker on The Mirror ── */}
      {aicci && (
        <div className="rounded-2xl overflow-hidden mir-rise" style={{ background: "linear-gradient(135deg, #0a0c1e 0%, #0f1228 50%, #0c0e20 100%)", border: "1px solid rgba(139,92,246,0.12)", animationDelay: "0.3s" }} data-testid="mirror-aicci">
          <div className="p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-4">
                {/* Score ring */}
                <div className="relative w-20 h-20 flex items-center justify-center flex-shrink-0">
                  <svg width="80" height="80" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="5" />
                    <circle cx="40" cy="40" r="34" fill="none"
                      stroke={aicci.score >= 60 ? "#34d399" : aicci.score >= 40 ? "#f59e0b" : "#f87171"}
                      strokeWidth="5" strokeDasharray={`${aicci.score * 2.14} ${214 - aicci.score * 2.14}`}
                      strokeDashoffset="53.5" strokeLinecap="round" />
                  </svg>
                  <span className="absolute text-2xl font-black" style={{ color: aicci.score >= 60 ? "#34d399" : aicci.score >= 40 ? "#f59e0b" : "#f87171" }}>{aicci.score}</span>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-violet-400 uppercase tracking-[0.2em]">Conscious Innovation Index</p>
                  <p className="text-sm text-slate-300 leading-snug max-w-md">{aicci.interpretation}</p>
                </div>
              </div>
            </div>

            {/* Dimension bars */}
            <div className="space-y-2.5">
              {Object.entries(aicci.dimensions).map(([key, dim]) => {
                const colors = { care: "#34d399", diversity: "#ec4899", governance: "#8b5cf6", geographic: "#3b82f6", extraction: "#f59e0b", inclusion: "#06b6d4", agency: "#f97316" };
                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-400 w-28 flex-shrink-0 font-medium">{dim.label}</span>
                    <div className="flex-1 h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${dim.score}%`, background: colors[key] || "#8b5cf6", transition: "width 1s ease" }} />
                    </div>
                    <span className="text-xs font-bold w-10 text-right" style={{ color: colors[key] || "#8b5cf6" }}>{dim.score}</span>
                  </div>
                );
              })}
            </div>

            {/* AICCI Insights */}
            {aicciInsights && (
              <div className="mt-5 pt-4 border-t border-white/[0.06] space-y-3">
                <p className="text-xs font-bold text-violet-400 uppercase tracking-wider">{aicciInsights.headline}</p>
                <p className="text-sm text-slate-300 leading-relaxed">{aicciInsights.key_insight}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-red-500/[0.05] border border-red-500/10 rounded-xl p-3">
                    <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-1">Biggest Gap</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{aicciInsights.biggest_gap}</p>
                  </div>
                  <div className="bg-emerald-500/[0.05] border border-emerald-500/10 rounded-xl p-3">
                    <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-1">Bright Spot</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{aicciInsights.bright_spot}</p>
                  </div>
                </div>
                {aicciInsights.action_items?.length > 0 && (
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">What must change</p>
                    {aicciInsights.action_items.map((item, i) => (
                      <div key={`action-${i}-${item.slice(0,20)}`} className="flex items-start gap-2 mb-1.5 last:mb-0">
                        <span className="text-violet-400 font-bold text-xs mt-0.5">{i + 1}.</span>
                        <p className="text-[11px] text-slate-400">{item}</p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-400 italic text-center">{aicciInsights.score_context}</p>
              </div>
            )}

            <div className="flex items-center justify-between mt-4">
              <p className="text-[10px] text-slate-500">Informed by <span className="text-slate-400">UN SDGs</span>, <span className="text-slate-400">OECD AI Principles</span>, <span className="text-slate-400">IEEE EAD</span>, <span className="text-slate-400">WEF</span>, <span className="text-slate-400">EU AI Act</span>, <span className="text-slate-400">UNCTAD</span>. {aicci.total_apps?.toLocaleString()} entities (apps + devices).</p>
              <AICCIMethodologyLink />
            </div>
          </div>
        </div>
      )}


      {/* Industry Consciousness Mirror */}
      <IndustryMirror />

      {/* The Trajectory: Where Is Humanity Headed? */}
      {trajectory && (
        <div className="space-y-4 mir-rise" style={{ animationDelay: "0.36s" }} data-testid="mirror-trajectory">
          {/* Header */}
          <div className="text-center py-8 rounded-2xl" style={{ background: "linear-gradient(135deg, #1a0a2e 0%, #0f1629 50%, #0a1e28 100%)", border: "1px solid rgba(245,158,11,0.15)" }}>
            <p className="text-xs font-bold text-amber-400 uppercase tracking-[0.25em] mb-2">Where is humanity headed?</p>
            <h3 className="text-2xl font-bold text-white mb-3">The Trajectory</h3>
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="text-3xl font-black" style={{ color: trajectory.direction === "ascending" ? "#10b981" : trajectory.direction === "declining" ? "#ef4444" : "#f59e0b" }}>
                {trajectory.direction === "ascending" ? "\u2191" : trajectory.direction === "declining" ? "\u2193" : "\u2192"}
              </span>
              <span className="text-sm font-bold capitalize" style={{ color: trajectory.direction === "ascending" ? "#10b981" : trajectory.direction === "declining" ? "#ef4444" : "#f59e0b" }}>
                {trajectory.direction}
              </span>
            </div>
            <p className="text-xs text-slate-400 max-w-lg mx-auto">{trajectory.direction_reason}</p>
          </div>

          {/* Verdict */}
          <div className="bg-amber-500/[0.06] border border-amber-500/20 rounded-2xl p-6 text-center">
            <p className="text-base text-white font-semibold leading-relaxed italic">"{trajectory.verdict}"</p>
            <div className="flex items-center justify-center gap-8 mt-4">
              <div className="text-center">
                <p className="text-2xl font-black text-violet-400">{trajectory.current_cii}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Today</p>
              </div>
              <div className="text-slate-500">{"\u2192"}</div>
              <div className="text-center">
                <p className="text-2xl font-black text-blue-400">{trajectory.projected_2028}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">2028</p>
              </div>
              <div className="text-slate-500">{"\u2192"}</div>
              <div className="text-center">
                <p className="text-2xl font-black text-emerald-400">{trajectory.projected_2030}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">2030</p>
              </div>
            </div>
          </div>

          {/* Three Scenarios */}
          {trajectory.scenarios && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: "optimistic", color: "#10b981", icon: "\u2191\u2191" },
                { key: "current_path", color: "#f59e0b", icon: "\u2192" },
                { key: "pessimistic", color: "#ef4444", icon: "\u2193\u2193" },
              ].map(({ key, color, icon }) => {
                const s = trajectory.scenarios[key];
                if (!s) return null;
                return (
                  <div key={key} className="rounded-xl p-5" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">{icon}</span>
                      <div>
                        <p className="text-xs font-bold text-white">{s.title}</p>
                        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>{s.probability} probability</p>
                      </div>
                      <span className="ml-auto text-xl font-black" style={{ color }}>{s.cii}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{s.description}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tipping Points */}
          {trajectory.tipping_points?.length > 0 && (
            <div className="bg-white/[0.02] border border-violet-500/15 rounded-xl p-5">
              <p className="text-[10px] font-bold text-violet-400 uppercase tracking-[0.2em] mb-4">Tipping Points</p>
              <div className="space-y-3">
                {trajectory.tipping_points.map((tp, i) => (
                  <div key={`tp-${i}-${tp.slice(0,20)}`} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-violet-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-black text-violet-400">{i + 1}</span>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed">{tp}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What Must Change */}
          {trajectory.what_must_change && (
            <div className="bg-white/[0.02] border border-amber-500/10 rounded-xl p-5 text-center">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-[0.2em] mb-2">What Must Change</p>
              <p className="text-sm text-slate-300 leading-relaxed">{trajectory.what_must_change}</p>
            </div>
          )}
        </div>
      )}

      {/* Vision 2050 */}
      <div className="bg-white/[0.02] border border-amber-500/10 rounded-2xl p-6 mir-rise" style={{ animationDelay: "0.35s" }} data-testid="mirror-reality-check">
        <p className="text-xs font-bold text-amber-400 uppercase tracking-[0.2em] mb-4">The Reality of Building AI</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-amber-400">48%</p>
            <p className="text-[10px] text-slate-400 mt-1">of AI projects survive from prototype to production</p>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-red-400">95%</p>
            <p className="text-[10px] text-slate-400 mt-1">of AI initiatives fail to turn a clear profit (MIT)</p>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-blue-400">50K+</p>
            <p className="text-[10px] text-slate-400 mt-1">active AI tools worldwide, including private systems</p>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-violet-400">254</p>
            <p className="text-[10px] text-slate-400 mt-1">Physical AI scaleup companies as of 2026</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-emerald-400">1,451</p>
            <p className="text-[10px] text-slate-400 mt-1">FDA-cleared AI/ML medical devices (2025)</p>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-cyan-400">542K</p>
            <p className="text-[10px] text-slate-400 mt-1">industrial robot installations in 2024</p>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-pink-400">9%</p>
            <p className="text-[10px] text-slate-400 mt-1">of IoT devices (16B+) have on-board AI</p>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-xl font-black text-orange-400">2/3</p>
            <p className="text-[10px] text-slate-400 mt-1">of AI compute will be inference by 2026 (IBM)</p>
          </div>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          For every AI product that reaches users, dozens more fail silently. 30% of generative AI projects are abandoned after proof-of-concept. Yet by 2026, inference dominates compute and Physical AI moves from labs to production. The question is not whether convergence will happen, but whose values it will carry.
        </p>
        <p className="text-[10px] text-slate-500 mt-2">Foundational references: MIT Sloan, Forbes, Crunchbase, Cervicorn Consulting, FDA, IBM Research, Stanford HAI, IFR World Robotics 2024-2026</p>
      </div>


      {/* Orientation trend — flowing area chart */}
      {trends?.orientation_trends?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 mir-rise" style={{ animationDelay: "0.4s" }}>
          <h3 className="text-sm font-bold text-slate-300 mb-1">What is humanity solving for?</h3>
          <p className="text-[11px] text-slate-500 mb-4">How our priorities are shifting, by orientation over time</p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends.orientation_trends.slice(-7)} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="mir-care" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#34d399" stopOpacity={0.3} /><stop offset="95%" stopColor="#34d399" stopOpacity={0} /></linearGradient>
                  <linearGradient id="mir-util" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3} /><stop offset="95%" stopColor="#60a5fa" stopOpacity={0} /></linearGradient>
                  <linearGradient id="mir-creat" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#a78bfa" stopOpacity={0.3} /><stop offset="95%" stopColor="#a78bfa" stopOpacity={0} /></linearGradient>
                  <linearGradient id="mir-extr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f87171" stopOpacity={0.3} /><stop offset="95%" stopColor="#f87171" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="year" stroke="rgba(255,255,255,0.15)" tick={{ fill: "#64748b", fontSize: 11 }} />
                <YAxis stroke="rgba(255,255,255,0.15)" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                <Tooltip
                  contentStyle={{ background: "rgba(10,15,25,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 11 }}
                  labelStyle={{ color: "#e2e8f0", fontWeight: 700 }}
                  formatter={(v, name) => [`${v}%`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Area type="monotone" dataKey="care_pct" name="Care" stroke="#34d399" strokeWidth={2.5} fill="url(#mir-care)" dot={{ r: 4, fill: "#34d399", strokeWidth: 0 }} activeDot={{ r: 6, stroke: "#34d399", strokeWidth: 2, fill: "#0a1628" }} animationDuration={1500} />
                <Area type="monotone" dataKey="utility_pct" name="Utility" stroke="#60a5fa" strokeWidth={2.5} fill="url(#mir-util)" dot={{ r: 4, fill: "#60a5fa", strokeWidth: 0 }} activeDot={{ r: 6 }} animationDuration={1500} animationBegin={200} />
                <Area type="monotone" dataKey="creative_pct" name="Creative" stroke="#a78bfa" strokeWidth={2.5} fill="url(#mir-creat)" dot={{ r: 4, fill: "#a78bfa", strokeWidth: 0 }} activeDot={{ r: 6 }} animationDuration={1500} animationBegin={400} />
                <Area type="monotone" dataKey="extraction_pct" name="Extraction" stroke="#f87171" strokeWidth={2.5} fill="url(#mir-extr)" dot={{ r: 4, fill: "#f87171", strokeWidth: 0 }} activeDot={{ r: 6 }} animationDuration={1500} animationBegin={600} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Provocative Question + Viewer Response — conversational style */}
      <div className="relative overflow-hidden rounded-2xl"
        style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0a1628 100%)", border: "1px solid rgba(139,92,246,0.15)" }}>
        <div className="p-6 md:p-8">
          <div className="text-center mb-5">
            <p className="text-[10px] font-bold text-violet-400 uppercase tracking-[0.2em] mb-3">We ask you</p>
            <h3 className="text-lg md:text-xl font-bold text-white leading-snug max-w-lg mx-auto">
              {prediction?.provocative_question || "If the technology we build is a mirror of our values, are you proud of what you see?"}
            </h3>
          </div>

          {!submitted ? (
            <div className="max-w-md mx-auto">
              {/* Emotion selector — bigger, clearer */}
              <p className="text-xs text-slate-400 text-center mb-3">How does this make you feel?</p>
              <div className="flex justify-center gap-2 mb-5">
                {EMOTIONS.map((e) => (
                  <button key={e.id} onClick={() => setSelectedEmotion(e.id)}
                    data-testid={`emotion-${e.id}`}
                    className={`px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
                      selectedEmotion === e.id
                        ? "border-white/30 text-white scale-105"
                        : "border-white/[0.08] text-slate-400 hover:border-white/20 hover:text-slate-300"
                    }`}
                    style={selectedEmotion === e.id ? { background: `${e.color}20`, borderColor: `${e.color}50`, color: e.color } : {}}>
                    {e.label}
                  </button>
                ))}
              </div>
              {/* Text input — chat-like */}
              <div className="relative mb-3">
                <textarea value={userResponse} onChange={(e) => setUserResponse(e.target.value)}
                  placeholder="What direction would you advise humanity to take with AI?"
                  rows={2} data-testid="mirror-response-input"
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.1] rounded-2xl text-sm resize-none focus:outline-none focus:border-violet-500/40 placeholder:text-slate-500 text-white pr-20"
                  style={{ minHeight: 48 }} />
                <button onClick={handleSubmitResponse} disabled={(!userResponse.trim() && !selectedEmotion) || submitting}
                  data-testid="mirror-submit-btn"
                  className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5 px-4 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-30 text-white text-xs font-semibold rounded-xl transition-all">
                  {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Send
                </button>
              </div>
              <p className="text-[10px] text-slate-500 text-center">Your voice shapes the conversation. Anonymous and respected.</p>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 mx-auto rounded-full bg-violet-500/15 flex items-center justify-center mb-3">
                <Heart className="w-6 h-6 text-violet-400" />
              </div>
              <p className="text-sm text-slate-300 font-medium">Thank you for reflecting with us</p>
              <p className="text-xs text-slate-400 mt-1">Your voice matters in shaping the future of AI</p>
            </div>
          )}
        </div>
      </div>

      {/* Community Responses */}
      {responses && responses.total_responses > 0 && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6" data-testid="mirror-community">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-bold text-slate-300">What the community feels</h3>
            <span className="text-xs text-slate-500 ml-auto">{responses.total_responses} voices</span>
          </div>
          {/* Emotion breakdown */}
          <div className="flex gap-3 mb-4">
            {EMOTIONS.map((e) => {
              const count = responses.emotions[e.id] || 0;
              const pct = responses.total_responses > 0 ? Math.round(count / responses.total_responses * 100) : 0;
              return (
                <div key={e.id} className="flex-1 bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
                  <span className="text-lg font-bold" style={{ color: e.color }}>{pct}%</span>
                  <p className="text-[10px] text-slate-400 mt-0.5">{e.label}</p>
                </div>
              );
            })}
          </div>
          {/* Recent written responses */}
          {responses.recent?.filter(r => r.response).length > 0 && (
            <div className="space-y-2">
              {responses.recent.filter(r => r.response).slice(0, 5).map((r, i) => (
                <div key={`resp-${i}-${r.emotion}`} className="flex items-start gap-2 py-2 border-b border-white/[0.03] last:border-0">
                  <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: EMOTIONS.find(e => e.id === r.emotion)?.color || "#94a3b8" }} />
                  <p className="text-xs text-slate-400 leading-relaxed">{r.response}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Floating Word Cloud from community responses ── */}
      {wordcloud?.words?.length > 0 && (
        <div className="relative overflow-hidden rounded-2xl py-10 px-6"
          style={{ background: "linear-gradient(180deg, #080e1a 0%, #0a0e18 100%)", border: "1px solid rgba(255,255,255,0.04)", minHeight: 200 }}
          data-testid="mirror-wordcloud">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] text-center mb-6">What humanity is saying</p>
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 max-w-3xl mx-auto">
            {wordcloud.words.map((w, i) => {
              const colors = ["#34d399", "#60a5fa", "#a78bfa", "#f87171", "#fbbf24", "#22d3ee", "#f472b6"];
              return (
                <span key={w.text} className="inline-block transition-all hover:scale-110 cursor-default"
                  style={{
                    fontSize: w.size, fontWeight: w.size > 30 ? 800 : w.size > 20 ? 600 : 400,
                    color: colors[i % colors.length], opacity: 0.6 + (w.size / 48) * 0.4,
                    animation: `cst-float ${3 + (i % 4)}s ease-in-out ${-(i * 0.3)}s infinite`,
                  }}>
                  {w.text}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Vision 2050: Framework-based Deep Prediction — always visible ── */}
      <div className="space-y-6" data-testid="mirror-vision2050">
        {/* Header */}
        <div className="text-center py-8 rounded-2xl mir-rise" style={{ background: "linear-gradient(135deg, #0a0e2a 0%, #0c1a3e 50%, #0a0e28 100%)", border: "1px solid rgba(96,165,250,0.15)", animationDelay: "0.6s" }}>
          <p className="text-xs font-bold text-blue-400 uppercase tracking-[0.25em] mb-2">Beyond what we see today</p>
          <h3 className="text-2xl font-bold text-white mb-3">The World in 2050</h3>
          <p className="text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">What our AI choices today tell us about the world we're building for tomorrow, analyzed through 5 established frameworks.</p>
        </div>

        {vision2050 ? (
          <>
            {/* The bold 2050 vision */}
          <div className="bg-blue-500/[0.04] border border-blue-500/15 rounded-2xl p-6">
            <p className="text-sm text-slate-200 leading-relaxed italic">"{vision2050.world_2050}"</p>
            <p className="text-xs text-blue-400 mt-3 font-medium">{vision2050.values_shift}</p>
          </div>

          {/* Framework cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Maslow */}
            <div className="bg-white/[0.02] border border-emerald-500/10 rounded-xl p-5 group relative hover:border-emerald-500/30 transition-colors">
              <div className="hidden group-hover:block absolute -top-10 left-0 right-0 z-10">
                <div className="mx-2 px-3 py-1.5 bg-black/90 border border-emerald-500/30 rounded-lg text-[10px] text-slate-300 text-center">{FRAMEWORK_DESC.maslow}</div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-md bg-emerald-500/15 flex items-center justify-center text-[10px] font-bold text-emerald-400">M</div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Maslow's Hierarchy</p>
                  <p className="text-xs font-bold text-emerald-400">{clean(vision2050.maslow?.current_level)}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">{vision2050.maslow?.insight}</p>
            </div>

            {/* Perez */}
            <div className="bg-white/[0.02] border border-blue-500/10 rounded-xl p-5 group relative hover:border-blue-500/30 transition-colors">
              <div className="hidden group-hover:block absolute -top-10 left-0 right-0 z-10">
                <div className="mx-2 px-3 py-1.5 bg-black/90 border border-blue-500/30 rounded-lg text-[10px] text-slate-300 text-center">{FRAMEWORK_DESC.perez}</div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-md bg-blue-500/15 flex items-center justify-center text-[10px] font-bold text-blue-400">P</div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Perez Tech Revolutions</p>
                  <p className="text-xs font-bold text-blue-400">{clean(vision2050.perez?.current_phase)}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">{vision2050.perez?.insight}</p>
            </div>

            {/* Schwartz */}
            <div className="bg-white/[0.02] border border-violet-500/10 rounded-xl p-5 group relative hover:border-violet-500/30 transition-colors">
              <div className="hidden group-hover:block absolute -top-10 left-0 right-0 z-10">
                <div className="mx-2 px-3 py-1.5 bg-black/90 border border-violet-500/30 rounded-lg text-[10px] text-slate-300 text-center">{FRAMEWORK_DESC.schwartz}</div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-md bg-violet-500/15 flex items-center justify-center text-[10px] font-bold text-violet-400">S</div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Schwartz Values Theory</p>
                  <p className="text-xs font-bold text-violet-400">{clean(vision2050.schwartz?.dominant_values)}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">{vision2050.schwartz?.insight}</p>
            </div>

            {/* UN SDGs */}
            <div className="bg-white/[0.02] border border-cyan-500/10 rounded-xl p-5 group relative hover:border-cyan-500/30 transition-colors">
              <div className="hidden group-hover:block absolute -top-10 left-0 right-0 z-10">
                <div className="mx-2 px-3 py-1.5 bg-black/90 border border-cyan-500/30 rounded-lg text-[10px] text-slate-300 text-center">{FRAMEWORK_DESC.sdgs}</div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-md bg-cyan-500/15 flex items-center justify-center text-[10px] font-bold text-cyan-400">UN</div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">UN SDGs</p>
                </div>
              </div>
              <div className="mb-2">
                <p className="text-[10px] text-emerald-400 font-semibold mb-0.5">Accelerating:</p>
                <p className="text-[10px] text-slate-400">{vision2050.sdgs?.accelerating?.join(", ")}</p>
              </div>
              <div className="mb-2">
                <p className="text-[10px] text-red-400 font-semibold mb-0.5">Neglecting:</p>
                <p className="text-[10px] text-slate-400">{vision2050.sdgs?.neglecting?.join(", ")}</p>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">{vision2050.sdgs?.insight}</p>
            </div>

            {/* Spiral Dynamics */}
            <div className="bg-white/[0.02] border border-amber-500/10 rounded-xl p-5 md:col-span-2 lg:col-span-1 group relative hover:border-amber-500/30 transition-colors">
              <div className="hidden group-hover:block absolute -top-10 left-0 right-0 z-10">
                <div className="mx-2 px-3 py-1.5 bg-black/90 border border-amber-500/30 rounded-lg text-[10px] text-slate-300 text-center">{FRAMEWORK_DESC.spiral}</div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-md bg-amber-500/15 flex items-center justify-center text-[10px] font-bold text-amber-400">SD</div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Spiral Dynamics</p>
                  <p className="text-xs font-bold text-amber-400">{clean(vision2050.spiral?.current_level)}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">{vision2050.spiral?.insight}</p>
            </div>
          </div>

          {/* Call to action */}
          <div className="text-center py-6">
            <p className="text-base text-white font-bold max-w-lg mx-auto leading-relaxed">{vision2050.call_to_action}</p>
          </div>
          </>
        ) : (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white/[0.02] border border-white/[0.04] rounded-xl p-5 animate-pulse">
                <div className="h-3 bg-white/[0.06] rounded w-1/3 mb-3" />
                <div className="h-2 bg-white/[0.04] rounded w-full mb-2" />
                <div className="h-2 bg-white/[0.04] rounded w-2/3" />
              </div>
            ))}
            <p className="text-xs text-slate-500 text-center">Generating 2050 vision from framework analysis...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MirrorPage;

/* ─── Industry Mirror ─── */
const IndustryMirror = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/industry/aicci`).then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.length === 0) return null;
  const top = data[0];
  const bottom = data[data.length - 1];
  return (
    <div className="bg-white/[0.02] border border-amber-500/10 rounded-2xl p-6 mir-rise" style={{ animationDelay: "0.32s" }} data-testid="mirror-industry">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-4 h-4 text-amber-400" />
        <p className="text-xs font-bold text-amber-400 uppercase tracking-[0.2em]">Industry Consciousness Mirror</p>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed mb-4">
        <span className="text-emerald-400 font-semibold">{top.industry}</span> leads with {top.care_pct}% care orientation and an AICCI of {top.aicci}.
        Meanwhile, <span className="text-red-400 font-semibold">{bottom.industry}</span> scores just {bottom.aicci} with {bottom.extraction_pct}% extraction.
        The gap reveals which industries build AI to serve people and which build it to extract from them.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.slice(0, 4).map((ind, i) => (
          <div key={ind.industry} className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-lg font-black" style={{ color: ind.aicci >= 70 ? "#10b981" : ind.aicci >= 40 ? "#f59e0b" : "#ef4444" }}>{ind.aicci}</p>
            <p className="text-[10px] text-slate-400 mt-1 leading-tight">{ind.industry}</p>
            <p className="text-[10px] text-emerald-400/60">{ind.care_pct}% care</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        {data.slice(-4).reverse().map((ind, i) => (
          <div key={ind.industry} className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <p className="text-lg font-black" style={{ color: ind.aicci >= 70 ? "#10b981" : ind.aicci >= 40 ? "#f59e0b" : "#ef4444" }}>{ind.aicci}</p>
            <p className="text-[10px] text-slate-400 mt-1 leading-tight">{ind.industry}</p>
            <p className="text-[10px] text-red-400/60">{ind.extraction_pct}% extraction</p>
          </div>
        ))}
      </div>
    </div>
  );
};
